import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { peopleApi, PersonNames, RoleMailbox } from '../../utils/api/people';

// Admin → Users → Names & greetings / Personal mailboxes (server: routes/people.ts). What used to be name
// lists in the code (multi-tenant, owner 2026-10-03): the other names a person goes by (users.aliases —
// job-comment pings, staff folders), how emails greet them (users.greeting) and which role-looking mailbox
// is one person's own (accounts.personal_mailbox). Who receives what is a role (Admin → Roles).

const card: React.CSSProperties = { background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: 16, marginTop: 16 };
const field: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '6px 8px', fontSize: 12.5, borderRadius: 8, border: '1px solid var(--border-strong)',
  background: 'var(--bg-surface)', color: 'var(--text-primary)', outline: 'none',
};
const btn = (bg: string, color = '#fff'): React.CSSProperties => ({
  padding: '6px 12px', borderRadius: 'var(--radius-md)', border: 'none', background: bg, color, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
});
const th: React.CSSProperties = { textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', padding: '6px 8px', borderBottom: '1px solid var(--border)' };
const td: React.CSSProperties = { padding: '6px 8px', fontSize: 12.5, verticalAlign: 'top', borderBottom: '1px solid var(--border)' };

function NamesRow({ u, onSaved }: { u: PersonNames; onSaved: (u: PersonNames) => void }) {
  const { t } = useTranslation();
  const [aliases, setAliases] = useState(u.aliases.join(', '));
  const [greeting, setGreeting] = useState(u.greeting);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(''); const [err, setErr] = useState('');
  useEffect(() => { setAliases(u.aliases.join(', ')); setGreeting(u.greeting); }, [u]);
  const dirty = aliases !== u.aliases.join(', ') || greeting !== u.greeting;
  const save = async () => {
    setBusy(true); setErr(''); setMsg('');
    try {
      const r = await peopleApi.saveNames(u.id, { aliases, greeting });
      onSaved(r.user); setMsg(t('people_saved')); setTimeout(() => setMsg(''), 2000);
    } catch (e: any) { setErr(e.message || 'Failed'); } finally { setBusy(false); }
  };
  const fallback = u.nickname || u.name || u.email.split('@')[0];
  return (
    <tr style={{ opacity: u.active ? 1 : 0.6 }}>
      <td style={{ ...td, minWidth: 180 }}>
        <div style={{ fontWeight: 600, color: 'var(--text-primary)', wordBreak: 'break-all' }}>{u.email}</div>
        <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{u.name}{u.nickname && u.nickname !== u.name ? ` (${u.nickname})` : ''}</div>
      </td>
      <td style={{ ...td, minWidth: 160 }}>
        <input value={aliases} onChange={e => setAliases(e.target.value)} placeholder={t('people_aliases_ph')} style={field} />
      </td>
      <td style={{ ...td, minWidth: 140 }}>
        <input value={greeting} onChange={e => setGreeting(e.target.value)} placeholder={t('people_greeting_ph', { name: fallback })} maxLength={60} style={field} />
        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 3 }}>{t('people_greets_as', { name: u.greetsAs || fallback })}</div>
      </td>
      <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }}>
        {err && <div style={{ fontSize: 11.5, color: '#E24B4A', marginBottom: 4, whiteSpace: 'normal' }}>{err}</div>}
        {msg ? <span style={{ fontSize: 12, color: '#1D9E75' }}><i className="ti ti-check" /> {msg}</span>
          : <button onClick={save} disabled={!dirty || busy} style={{ ...btn(dirty ? 'var(--accent)' : 'var(--c-gray-100)', dirty ? '#fff' : 'var(--text-tertiary)'), cursor: dirty ? 'pointer' : 'default' }}>{t('people_save')}</button>}
      </td>
    </tr>
  );
}

export default function PeopleSettings() {
  const { t } = useTranslation();
  const [users, setUsers] = useState<PersonNames[] | null>(null);
  const [mailboxes, setMailboxes] = useState<RoleMailbox[]>([]);
  const [showDisabled, setShowDisabled] = useState(false);
  const [err, setErr] = useState('');
  const load = () => peopleApi.settings().then(d => { setUsers(d.users); setMailboxes(d.mailboxes); }).catch((e: any) => setErr(e.message));
  useEffect(() => { load(); }, []);

  const toggle = async (m: RoleMailbox) => {
    setErr('');
    try {
      await peopleApi.setPersonal(m.email, !m.personal);
      setMailboxes(list => list.map(x => (x.email === m.email ? { ...x, personal: !m.personal } : x)));
    } catch (e: any) { setErr(e.message || 'Failed'); }
  };

  if (err && !users) return <div style={{ ...card, color: '#E24B4A', fontSize: 12.5 }}>{err}</div>;
  if (!users) return null;
  const disabled = users.filter(u => !u.active).length;
  const shown = users.filter(u => u.active || showDisabled);
  return (
    <>
      <div style={card}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{t('people_names_title')}</div>
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', margin: '4px 0 10px', lineHeight: 1.5 }}>{t('people_names_hint')}</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={th}>{t('people_col_login')}</th><th style={th}>{t('people_col_aliases')}</th><th style={th}>{t('people_col_greeting')}</th><th style={th} />
            </tr></thead>
            <tbody>
              {shown.map(u => <NamesRow key={u.id} u={u} onSaved={nu => setUsers(list => (list || []).map(x => (x.id === nu.id ? nu : x)))} />)}
              {shown.length === 0 && <tr><td style={{ ...td, color: 'var(--text-tertiary)' }} colSpan={4}>{t('people_none')}</td></tr>}
            </tbody>
          </table>
        </div>
        {disabled > 0 && (
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)', marginTop: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={showDisabled} onChange={e => setShowDisabled(e.target.checked)} />{t('people_show_disabled', { n: disabled })}
          </label>
        )}
        <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 8 }}><i className="ti ti-info-circle" /> {t('people_roles_note')}</div>
      </div>
      <div style={card}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{t('people_mailboxes_title')}</div>
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', margin: '4px 0 10px', lineHeight: 1.5 }}>{t('people_mailboxes_hint')}</div>
        {err && <div style={{ fontSize: 12, color: '#E24B4A', marginBottom: 6 }}>{err}</div>}
        {mailboxes.length === 0 && <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>{t('people_mailboxes_none')}</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {mailboxes.map(m => (
            <label key={m.email} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-primary)', cursor: 'pointer', flexWrap: 'wrap' }}>
              <input type="checkbox" checked={m.personal} onChange={() => toggle(m)} />
              <span style={{ fontWeight: 600, wordBreak: 'break-all' }}>{m.email}</span>
              <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{t('people_mailbox_personal')}</span>
            </label>
          ))}
        </div>
      </div>
    </>
  );
}
